import React, { useEffect, useMemo, useState } from 'react';
import { Clock, Sparkles } from 'lucide-react';

// Fecha objetivo: 8 de agosto de 2026, 00:00 hora de Chile (UTC-4).
// Se calcula desde una fecha ISO absoluta para no depender de la zona del cliente.
const TARGET_ISO = '2026-08-08T00:00:00-04:00';

function computeParts(nowMs, targetMs) {
    const diff = Math.max(0, targetMs - nowMs);
    const totalSec = Math.floor(diff / 1000);
    const days = Math.floor(totalSec / 86400);
    const hours = Math.floor((totalSec % 86400) / 3600);
    const minutes = Math.floor((totalSec % 3600) / 60);
    const seconds = totalSec % 60;
    return { diff, days, hours, minutes, seconds, expired: diff === 0 };
}

function Block({ value, label, testid }) {
    return (
        <div className="flex flex-col items-center" data-testid={testid}>
            <div className="min-w-[64px] rounded-lg bg-white/95 px-3 py-2 text-center shadow-inner ring-1 ring-white/60">
                <div className="font-mono text-3xl font-bold text-amber-900 leading-none tabular-nums">
                    {String(value).padStart(2, '0')}
                </div>
            </div>
            <div className="mt-1 text-[10px] font-semibold uppercase tracking-wider text-amber-100">
                {label}
            </div>
        </div>
    );
}

/**
 * PagoCountdownCard — Contador de días/horas/minutos hasta la activación del pago (8 ago 2026).
 * Se auto-actualiza cada 30 s. Se oculta 24 h después del vencimiento.
 */
export default function PagoCountdownCard() {
    const targetMs = useMemo(() => new Date(TARGET_ISO).getTime(), []);
    const [now, setNow] = useState(() => Date.now());

    useEffect(() => {
        const id = setInterval(() => setNow(Date.now()), 30000);
        return () => clearInterval(id);
    }, []);

    const { days, hours, minutes, expired } = computeParts(now, targetMs);
    // Ocultar el card 24 h después del vencimiento
    if (expired && now - targetMs > 24 * 3600 * 1000) return null;

    if (expired) {
        return (
            <div
                className="rounded-2xl border-2 border-emerald-300 bg-gradient-to-br from-emerald-600 via-emerald-700 to-teal-800 p-4 sm:p-5 shadow-lg text-white"
                data-testid="pago-countdown-expired"
                role="status"
            >
                <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/15">
                        <Sparkles className="h-5 w-5" />
                    </div>
                    <div>
                        <p className="font-heading text-base font-bold">¡La versión completa ya está activa!</p>
                        <p className="text-xs text-emerald-100">Activa tu plan para seguir construyendo tu Portafolio con acompañamiento profesional.</p>
                    </div>
                </div>
            </div>
        );
    }

    // Estados de urgencia:
    // - ≥ 8 días  → tono azul-slate sobrio
    // - 4-7 días  → tono ámbar
    // - ≤ 3 días  → tono rojo con pulso
    const critical = days <= 3;
    const wrap = critical
        ? 'border-rose-300 bg-gradient-to-br from-rose-700 via-rose-800 to-amber-900 shadow-rose-900/30'
        : days <= 7
            ? 'border-amber-300 bg-gradient-to-br from-amber-600 via-amber-700 to-orange-800 shadow-amber-900/30'
            : 'border-slate-300 bg-gradient-to-br from-slate-800 via-slate-900 to-indigo-950 shadow-slate-900/30';

    return (
        <div
            className={`relative overflow-hidden rounded-2xl border-2 p-4 sm:p-5 shadow-lg text-white ${wrap} ${critical ? 'animate-pulse-slow' : ''}`}
            data-testid="pago-countdown-card"
            role="status"
        >
            {/* Decoración sutil */}
            <div className="pointer-events-none absolute -right-8 -top-8 h-32 w-32 rounded-full bg-white/5" aria-hidden />
            <div className="pointer-events-none absolute -left-6 -bottom-10 h-24 w-24 rounded-full bg-white/5" aria-hidden />

            <div className="relative flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-start gap-3">
                    <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/15">
                        <Clock className="h-5 w-5" />
                    </div>
                    <div>
                        <p className="font-heading text-sm sm:text-base font-bold leading-tight">
                            {critical ? '¡Últimos días de acceso libre!' : 'Acceso libre por tiempo limitado'}
                        </p>
                        <p className="text-xs sm:text-sm text-white/85 leading-snug mt-0.5">
                            {critical
                                ? <>El <strong>viernes 8 de agosto</strong> se activa el pago. Todo tu avance quedará guardado — asegura tu lugar antes.</>
                                : <>Contador hasta la activación del pago el <strong>viernes 8 de agosto de 2026</strong>.</>}
                        </p>
                    </div>
                </div>

                <div className="flex items-end gap-2 self-start sm:self-auto" data-testid="pago-countdown-blocks">
                    <Block value={days} label="Días" testid="pago-countdown-days" />
                    <div className="pb-6 text-2xl font-bold text-white/60">:</div>
                    <Block value={hours} label="Horas" testid="pago-countdown-hours" />
                    <div className="pb-6 text-2xl font-bold text-white/60">:</div>
                    <Block value={minutes} label="Min" testid="pago-countdown-minutes" />
                </div>
            </div>
        </div>
    );
}
